import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "../../providers/Redux/Reducers/AppContext";
import { RouteComponentProps, useParams } from "react-router-dom";
// import { menuController } from '@ionic/core';
// Import Ionic Components
import { IonCol, IonGrid, IonIcon, IonInfiniteScroll, IonInfiniteScrollContent, IonRow } from "@ionic/react";

// Import Icons
import { logoDropbox } from "ionicons/icons";

// Import Css File
import "./Products.scss";
import styles from "./Products.module.scss";
import classNames from "classnames/bind";

// Import Components

import { SendUserRequestFormFab } from "../../components/SendUserRequestFormFab/SendUserRequestFormFab";
import MaxWidthContainer from "../../components/UI/MaxWidthContainer/MaxWidthContainer";
import PageTitle from "../../components/PageTitle/PageTitle";
import { generateObjectsWithId } from "../../utils/utils";
import { useGetCategoryByIdQuery } from "../../providers/categories";
import ProductSkeleton from "../../components/UI/ProductSkeleton/ProductSkeleton";
import { useGetProductsInfiniteQuery } from "../../providers/products";
import { useConfig } from "../../providers/config";
import Page from "../page-layout";
import { NewListViewProduct } from "@core/components";
import { useActiveOrMainShippingZone } from "../../providers/shippingZones";
import { getQueryParamsFromPath, replaceEmbed } from "../../utils/getQueryParamsFromPath";
import { useTranslation } from "react-i18next";

const cx = classNames.bind(styles);

const skeletonProducts = generateObjectsWithId(12);

const defaultParams = {
  min_price: 0.1,
};

interface RouteParams {
  id: string;
  name: string;
  type: string;
}

const Products: React.FC<RouteComponentProps<RouteParams>> = () => {
  const params = useParams<RouteParams>();

  const queryParams = useMemo(() => getQueryParamsFromPath(params), [params]);

  const productsSkeleton = useMemo(
    () =>
      skeletonProducts.map((product) => (
        <IonCol
          key={product.id}
          size={"12"}
          sizeSm={"4"}
          sizeMd={"3"}
          sizeLg={"2.4"}
          sizeXl={"2"}
          className={cx("productsRow__col")}
        >
          <ProductSkeleton />
        </IonCol>
      )),
    [],
  );

  const { activeShippingZone } = useActiveOrMainShippingZone();

  const { t } = useTranslation();
  const {
    data: productsResults,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isRefetching,
  } = useGetProductsInfiniteQuery({
    options: {
      enabled: !!queryParams && !!activeShippingZone.id,
    },
    params: {
      ...defaultParams,
      ...queryParams,
      pageNumber: "1",
      pageSize: "12",
      zoneId: activeShippingZone.id,
    },
  });

  const products = productsResults?.pages.flatMap((p) => p?.products ?? []);

  const infinite = useRef<HTMLIonInfiniteScrollElement | null>(null); // For Infinite Scrool Reference

  const [categoryDescription, setCategoryDescription] = useState<string | "">("");
  const config = useConfig();
  const { data: category } = useGetCategoryByIdQuery(params.id!, { enabled: params.id !== "0" });

  useEffect(() => {
    if (category?.description && params.id !== "0") {
      const processedDescription = replaceEmbed(category.description);
      setCategoryDescription(processedDescription === '""' ? "" : processedDescription);
    } else {
      setCategoryDescription("");
    }
  }, [category, params?.id]);

  return (
    <Page>
      <Page.HeaderMobile showBackButton />
      <Page.Content scrollEvents={true} className="ion-content" id="productContent">
        <Page.HeaderDesktop
          breadcrumbs={[
            {
              title: params.id !== "0" ? params.name : t(params.type),
            },
          ]}
        />
        <MaxWidthContainer classList={`global-content ${cx({ products__isEmpty: !products?.length })}`}>
          <PageTitle title={params.id !== "0" ? params.name : t(params.type)} />

          {categoryDescription && (
            <div>
              <div
                className={cx("categoryDescription__html")}
                dangerouslySetInnerHTML={{ __html: `${categoryDescription}` }}
              ></div>
            </div>
          )}

          {!!products?.some(Boolean) && !isRefetching && (
            <>
              <IonGrid>
                <IonRow className={cx("productsRow")}>
                  {products?.map((p) => {
                    return p ? (
                      <IonCol
                        key={p.id}
                        size={"12"}
                        sizeSm={"4"}
                        sizeMd={"3"}
                        sizeLg={"2.4"}
                        sizeXl={"2"}
                        className={cx("productsRow__col")}
                      >
                        <NewListViewProduct product={p} type="newList" />
                      </IonCol>
                    ) : null;
                  })}
                </IonRow>
              </IonGrid>
            </>
          )}

          {(isLoading || isRefetching) && (
            <IonGrid className={cx("width-100")}>
              <IonRow className={cx("productsRow")}>{productsSkeleton}</IonRow>
            </IonGrid>
          )}

          {!products?.some(Boolean) && !isLoading && !isRefetching && (
            <IonGrid className={cx("productsNotFound")}>
              <IonRow>
                <div className="flex flex-col justify-center items-center gap-2.5">
                  <IonIcon icon={logoDropbox} />
                  <h6>{t("No Products Found!")}</h6>
                </div>
              </IonRow>
            </IonGrid>
          )}

          <IonInfiniteScroll
            className={cx("infiniteScroll", "infiniteScroll--mobile")}
            // disabled={!hasNextPage && !isFetchingNextPage}
            threshold="100px"
            ref={infinite}
            onIonInfinite={async () => {
              if (hasNextPage) {
                await fetchNextPage();
              }
              infinite.current?.complete();
            }}
          >
            <IonInfiniteScrollContent loadingText={t("Please Wait")} loadingSpinner="bubbles" />
          </IonInfiniteScroll>
        </MaxWidthContainer>
        {config?.showSendCustomerRequestButton && <SendUserRequestFormFab />}
        <Page.ContentFooter />
      </Page.Content>
    </Page>
  );
};

export default Products;
